import { useContext, useState } from 'react';
import { useGetAllOrganisationsQuery } from './redux/organisationApiSlice';

import { Category, Handshake, History, Laptop, Person } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { CountBadge } from '../../components/CountBadge';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import InfoDrawer from '../../components/InfoDrawer';
import { OpenInNewTabButton } from '../../components/OpenInNewTabButton';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import { DetailedOrganisationResponse } from '../../models/Organisation';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { OrganisationInfoDialog } from './organisationButtons/details/OrganisationInfoDialog';
import { NotCorrectRights } from '../Permission/NotCorrectRights';
import { OrganisationContact, OrganisationContactLabel } from '../../models/OrganisationContact';

export const OrganisationList = () => {
    return (<OrganisationListContent />)
}

const OrganisationListContent = () => {
    const { t } = useTranslation()
    const ability = useContext(AbilityContext);

    const [clickedRow, setClickedRow] = useState<DetailedOrganisationResponse | undefined>(undefined)

    const canReadList = ability.can(PermissionEnum.ORGANISATION_READ, {})
    if (!canReadList)
        return <NotCorrectRights />

    const currentResult = useGetAllOrganisationsQuery({
        pagination: new PaginationQueryPackage()
    });

    if (currentResult.data === undefined)
        currentResult.data = []



    const getColumns = (organisations: DetailedOrganisationResponse[]) => {

        const contactGetter = (label: OrganisationContactLabel, mapper: (contact: OrganisationContact) => string) => {
            return (_: any, row: DetailedOrganisationResponse) => row.contacts?.filter(contact => contact.label === label).map(mapper).filter(value => value !== "").join("; ")
        }

        const columns: GridColDef[] = [
            { field: 'name', sortable: true, type: "string", headerName: t('Organisation'), flex: 3 },
            { field: 'address', sortable: true, type: "string", headerName: t('Address'), flex: 3 },
            { field: 'cvr', sortable: true, type: "string", headerName: t('CVR'), flex: 2 },
            { field: 'comment', sortable: true, type: "string", headerName: t('Remark'), flex: 2 },
            { field: 'ceo_name', sortable: true, type: "string", headerName: t('Overall contact name'), valueGetter: contactGetter(OrganisationContactLabel.CEO, contact => contact.name), flex: 2 },
            { field: 'ceo_email', sortable: true, type: "string", headerName: t('Overall contact email'), valueGetter: contactGetter(OrganisationContactLabel.CEO, contact => contact.email), flex: 2 },
            { field: 'ceo_phone', sortable: true, type: "string", headerName: t('Overall contact phone'), valueGetter: contactGetter(OrganisationContactLabel.CEO, contact => contact.phone), flex: 2 },
            { field: 'security_manager_name', sortable: true, type: "string", headerName: t('Security contact name'), valueGetter: contactGetter(OrganisationContactLabel.SECURITY_MANAGER, contact => contact.name), flex: 2 },
            { field: 'security_manager_email', sortable: true, type: "string", headerName: t('Security contact email'), valueGetter: contactGetter(OrganisationContactLabel.SECURITY_MANAGER, contact => contact.email), flex: 2 },
            { field: 'security_manager_phone', sortable: true, type: "string", headerName: t('Security contact phone'), valueGetter: contactGetter(OrganisationContactLabel.SECURITY_MANAGER, contact => contact.phone), flex: 2 },
            { field: 'organisational_managert_name', sortable: true, type: "string", headerName: t('Organisational contact name'), valueGetter: contactGetter(OrganisationContactLabel.ORGANISATIONAL_MANAGER, contact => contact.name), flex: 2 },
            { field: 'organisational_manager_email', sortable: true, type: "string", headerName: t('Organisational contact email'), valueGetter: contactGetter(OrganisationContactLabel.ORGANISATIONAL_MANAGER, contact => contact.email), flex: 2 },
            { field: 'organisational_manager_phone', sortable: true, type: "string", headerName: t('Organisational contact phone'), valueGetter: contactGetter(OrganisationContactLabel.ORGANISATIONAL_MANAGER, contact => contact.phone), flex: 2 },
            { field: 'financial_manager_name', sortable: true, type: "string", headerName: t('Financial contact name'), valueGetter: contactGetter(OrganisationContactLabel.FINANCIAL_MANAGER, contact => contact.name), flex: 2 },
            { field: 'financial_manager_email', sortable: true, type: "string", headerName: t('Financial contact email'), valueGetter: contactGetter(OrganisationContactLabel.FINANCIAL_MANAGER, contact => contact.email), flex: 2 },
            { field: 'financial_manager_phone', sortable: true, type: "string", headerName: t('Financial contact phone'), valueGetter: contactGetter(OrganisationContactLabel.FINANCIAL_MANAGER, contact => contact.phone), flex: 2 },
            { field: 'service_desk_email', sortable: true, type: "string", headerName: t('Service desk email'), valueGetter: contactGetter(OrganisationContactLabel.SERVICE_DESK, contact => contact.email), flex: 2 },
            { field: 'service_desk_phone', sortable: true, type: "string", headerName: t('Service desk telephone number'), valueGetter: contactGetter(OrganisationContactLabel.SERVICE_DESK, contact => contact.phone), flex: 2 },
            { field: 'overall_category', sortable: true, type: "string", headerName: t('Overall category'), valueGetter: (_, row: DetailedOrganisationResponse) => t(row.overall_category?.toString() ?? ""), flex: 2 },
            { field: 'specific_category', sortable: true, type: "string", headerName: t('Specific category'), valueGetter: (_, row: DetailedOrganisationResponse) => t(row.specific_category?.toString() ?? ""), flex: 2 },
            { field: 'invoice_ean', sortable: true, type: "string", headerName: t('Invoice - EAN'), valueGetter: (_, row: DetailedOrganisationResponse) => row.invoice.ean, flex: 2 },
            { field: 'invoice_email', sortable: true, type: "string", headerName: t('Invoice - email'), valueGetter: (_, row: DetailedOrganisationResponse) => row.invoice.email, flex: 2 },
            { field: 'invoice_reference', sortable: true, type: "string", headerName: t('Invoice - reference'), valueGetter: (_, row: DetailedOrganisationResponse) => row.invoice.reference, flex: 2 },
            { field: 'invoice_debitor', sortable: true, type: "string", headerName: t('Invoice - debitor'), valueGetter: (_, row: DetailedOrganisationResponse) => row.invoice.debitor, flex: 2 },
            { field: 'invoice_note', sortable: true, type: "string", headerName: t('Invoice - note'), valueGetter: (_, row: DetailedOrganisationResponse) => row.invoice.note, flex: 2 },
            { field: 'services_count', align: "center", renderCell: (params) => <CountBadge prefix={t("services")} icon={Category} count={params.row.services_count} />, sortable: true, type: "number", headerName: t('Services'), flex: 1 },
            { field: 'clients_count', align: "center", renderCell: (params) => <CountBadge prefix={t("clients")} icon={Laptop} count={params.row.clients_count} />, sortable: true, type: "number", headerName: t('Clients'), flex: 1 },
            { field: 'agreements_count', align: "center", renderCell: (params) => <CountBadge prefix={t("agreements")} icon={Handshake} count={params.row.agreements_count} />, sortable: true, type: "number", headerName: t('Agreements'), flex: 1 },
            { field: 'users_count', align: "center", renderCell: (params) => <CountBadge prefix={t("users")} icon={Person} count={params.row.users_count} />, sortable: true, type: "number", headerName: t('Users'), flex: 1 },
            {
                field: 'events', sortable: false, hideable: false, type: "string", valueGetter: () => "", headerName: t('Actions'), minWidth: 220, renderCell: (params => {
                    const organisation = organisations.find(x => x.uuid === params.id)!;
                    return (
                        <>
                            <InfoButton onClick={() => setClickedRow(organisation)} />
                            <Can I={PermissionEnum.AUDIT_READ} this={organisation}>
                                <OpenInNewTabButton tooltipText='Events' icon={<History />} url={`/events?entity_uuid=${organisation.uuid}`} />
                            </Can>
                            <OpenInNewTabButton url={`/organisations/${organisation.uuid}`} />
                        </>
                    )
                })
            }
        ];
        return columns
    }

    return (
        <>
            <DatagridWrapper
                dataGridId={DataGridId.ORGANISATIONS}
                getRowId={(row) => row.uuid}
                autoHeight
                loading={currentResult.isLoading}
                onRowClick={(row) => { setClickedRow(row.row) }}
                rows={currentResult.data}
                columns={getColumns(currentResult.data)}
                initialColumnVisibilityModel={{
                    cvr: false,
                    comment: false,
                    ceo_name: false,
                    ceo_phone: false,
                    ceo_email: false,
                    security_contact_name: false,
                    security_contact_email: false,
                    security_contact_phone: false,
                    organisational_contact_name: false,
                    organisational_contact_email: false,
                    organisational_contact_phone: false,
                    financial_contact_name: false,
                    financial_contact_email: false,
                    financial_contact_phone: false,
                    overall_category: false,
                    specific_category: false,
                    invoice_ean: false,
                    invoice_email: false,
                    invoice_reference: false,
                    invoice_debitor: false,
                    invoice_note: false,
                }}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <OrganisationInfoDialog
                        key={clickedRow.uuid}
                        organisationUuid={clickedRow.uuid}
                        afterDeleteAction={() => setClickedRow(undefined)} />}
            </InfoDrawer>
        </>
    )
}

